import { useEffect, useState } from "react";
import { useCurrentUser } from "hooks/shared";

type TaskAttachmentDownloadResult = [
    boolean
];

const useTaskAttachmentDownload: (
    dependenciesLoaded: boolean,
    attachments: { id: string, name: string, size: string }[]
) => TaskAttachmentDownloadResult = (dependenciesLoaded, attachments) => {
    const [loading, setLoading] = useState<boolean>(true);
    const user = useCurrentUser();
    const apibaseurl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchTaskAttachmentDownload = async () => {
            if (user && user.id_token && attachments && attachments.length > 0) {
                setLoading(true);

                for (const attachment of attachments) {
                    await handleAttachment(attachment);
                }

                setLoading(false);
            }
        };

        const handleAttachment = async (attachment: { id: string, name: string, size: string }) => {
            try {
                if (user && user.id_token && attachment?.id && attachment?.name) {
                    const response = await fetch(`${apibaseurl}/api/v3/attachments/${attachment.id}/download`, {
                        headers: {
                            Authorization: `Bearer ${user.id_token}`
                        }
                    });

                    if (!response.ok) {
                        console.error(`Failed to fetch ${attachment.name}`, response.status);
                        return;
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = attachment.name;

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    URL.revokeObjectURL(blobUrl);
                }
            } catch (error) {
                console.error('Error downloading attachment', error);
            }
        };

        if (dependenciesLoaded) {
            fetchTaskAttachmentDownload();
        }

        return () => {
            setLoading(false);
        };
    }, [dependenciesLoaded, user, attachments]);

    return [loading];
};

export default useTaskAttachmentDownload;